<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import ExportPdfButton from '../components/ExportPdfButton.vue';
import { atendimentoLegend, pdfNotes as buildNotes } from '../exportLegends';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import OverviewSection from '../../settings/reports/components/sections/OverviewSection.vue';
import ConversationsSection from '../../settings/reports/components/sections/ConversationsSection.vue';
import SummarySection from '../../settings/reports/components/sections/SummarySection.vue';
import CsatSection from '../../settings/reports/components/sections/CsatSection.vue';
import SLASection from '../../settings/reports/components/sections/SLASection.vue';

const { t } = useI18n();
const pageRef = ref(null);

const PERIODS = [
  { id: 'today', days: 0 },
  { id: 'last7days', days: 6 },
  { id: 'last30days', days: 29 },
  { id: 'last90days', days: 89 },
  { id: 'custom', days: null },
];

const selectedPeriod = ref('last30days');
const customFrom = ref('');
const customUntil = ref('');
const businessHoursOnly = ref(false);

const dateRange = computed(() => {
  if (selectedPeriod.value === 'custom') {
    return {
      since: customFrom.value ? startOfDay(new Date(customFrom.value)) : null,
      until: customUntil.value ? endOfDay(new Date(customUntil.value)) : null,
    };
  }

  const period = PERIODS.find(item => item.id === selectedPeriod.value);
  const today = new Date();
  return {
    since: startOfDay(subDays(today, period?.days ?? 29)),
    until: endOfDay(today),
  };
});

const formattedRange = computed(() => {
  if (!dateRange.value.since || !dateRange.value.until) return '';
  return `${format(dateRange.value.since, 'dd/MM/yyyy')} – ${format(dateRange.value.until, 'dd/MM/yyyy')}`;
});

const hasCompleteRange = computed(
  () => !!dateRange.value.since && !!dateRange.value.until
);

const pdfLegend = computed(() => atendimentoLegend(t));
const pdfNotes = computed(() => buildNotes(t, ['SNAPSHOT', 'BUSINESS_HOURS']));

const periodLabel = computed(() => {
  if (selectedPeriod.value === 'custom') return formattedRange.value;
  const name = t(
    `DASHBOARD.ATENDIMENTO.PERIOD.${selectedPeriod.value.toUpperCase()}`
  );
  return `${name} (${formattedRange.value})`;
});

// Seções na mesma ordem do menu de Relatórios (spec 143, Parte B). O corpo de
// cada uma chega em tarefas seguintes; por enquanto só o esqueleto navegável.
const SECTIONS = [
  {
    key: 'overview',
    live: true,
    defaultExpanded: true,
    component: OverviewSection,
  },
  {
    key: 'conversations',
    defaultExpanded: true,
    component: ConversationsSection,
    needsPeriod: true,
  },
  {
    key: 'agents',
    component: SummarySection,
    componentProps: { type: 'agent' },
    needsPeriod: true,
  },
  {
    key: 'labels',
    component: SummarySection,
    componentProps: { type: 'label' },
    needsPeriod: true,
  },
  {
    key: 'inboxes',
    component: SummarySection,
    componentProps: { type: 'inbox' },
    needsPeriod: true,
  },
  {
    key: 'teams',
    component: SummarySection,
    componentProps: { type: 'team' },
    needsPeriod: true,
  },
  {
    key: 'csat',
    component: CsatSection,
    needsPeriod: true,
  },
  {
    key: 'sla',
    component: SLASection,
    needsPeriod: true,
  },
];

const expandedSections = ref(
  new Set(SECTIONS.filter(section => section.defaultExpanded).map(s => s.key))
);

const isExpanded = key => expandedSections.value.has(key);

const toggleSection = key => {
  const next = new Set(expandedSections.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  expandedSections.value = next;
};

// Só as seções são vigiadas: o botão de exportar também tem um indicador de
// carregamento (enquanto exporta) e nunca deixaria a espera terminar.
const isStillLoading = () => {
  const sections = pageRef.value?.querySelector('[data-pdf-group]');
  return (
    !!sections?.querySelector('.animate-spin') ||
    /Carregando|Loading/i.test(sections?.innerText || '')
  );
};

// O PDF leva todas as seções abertas, com os dados carregados; depois da captura
// cada seção volta ao estado em que estava.
const prepareExport = async () => {
  const previous = expandedSections.value;
  expandedSections.value = new Set(SECTIONS.map(section => section.key));
  await nextTick();
  const deadline = Date.now() + 30000;
  await new Promise(resolve => {
    setTimeout(resolve, 1000);
  });
  while (isStillLoading() && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => {
      setTimeout(resolve, 500);
    });
  }
  return () => {
    expandedSections.value = previous;
  };
};

const sectionProps = section => {
  if (!section.needsPeriod) return { ...(section.componentProps || {}) };

  const period = {
    since: dateRange.value.since,
    until: dateRange.value.until,
  };
  // CSAT e SLA não têm conceito de horário comercial hoje (nem em Relatórios).
  const NO_BUSINESS_HOURS_SECTIONS = ['csat', 'sla', 'bot'];
  if (!NO_BUSINESS_HOURS_SECTIONS.includes(section.key)) {
    period.businessHours = businessHoursOnly.value;
  }

  return { ...period, ...(section.componentProps || {}) };
};

const goToSection = key => {
  if (!isExpanded(key)) toggleSection(key);
  document
    .getElementById(`painel-section-${key}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
</script>

<template>
  <div
    ref="pageRef"
    class="flex flex-col w-full h-full gap-4 p-6 overflow-y-auto"
  >
    <div data-pdf-ignore class="flex items-center justify-between gap-2">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('DASHBOARD.ATENDIMENTO.TITLE') }}
      </h1>
      <ExportPdfButton
        :target="pageRef"
        :page-title="t('DASHBOARD.ATENDIMENTO.TITLE')"
        slug="atendimento"
        :period-label="periodLabel"
        :filter-lines="
          businessHoursOnly ? [t('DASHBOARD.ATENDIMENTO.BUSINESS_HOURS')] : []
        "
        :notes="pdfNotes"
        :legend="pdfLegend"
        :before-export="prepareExport"
        :disabled="!hasCompleteRange"
      />
    </div>

    <div
      data-pdf-ignore
      class="flex flex-col gap-3 p-3 border rounded-lg lg:flex-row lg:items-center border-n-weak"
    >
      <div class="flex flex-wrap gap-1">
        <button
          v-for="period in PERIODS"
          :key="period.id"
          type="button"
          class="px-3 py-1 text-sm rounded-md"
          :class="
            selectedPeriod === period.id
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="selectedPeriod = period.id"
        >
          {{ t(`DASHBOARD.ATENDIMENTO.PERIOD.${period.id.toUpperCase()}`) }}
        </button>
      </div>

      <div v-if="selectedPeriod === 'custom'" class="flex items-center gap-2">
        <input
          v-model="customFrom"
          type="date"
          class="!h-8 !py-0 !mb-0 !text-sm rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
        <span class="text-n-slate-10">{{
          t('DASHBOARD.ATENDIMENTO.PERIOD.CUSTOM_UNTIL')
        }}</span>
        <input
          v-model="customUntil"
          type="date"
          class="!h-8 !py-0 !mb-0 !text-sm rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
      </div>

      <span v-if="formattedRange" class="text-xs text-n-slate-10">
        {{ formattedRange }}
      </span>

      <div class="flex items-center gap-2 lg:ltr:ml-auto lg:rtl:mr-auto">
        <span class="text-sm text-n-slate-11">
          {{ t('DASHBOARD.ATENDIMENTO.BUSINESS_HOURS') }}
        </span>
        <ToggleSwitch v-model="businessHoursOnly" />
      </div>
    </div>

    <nav data-pdf-ignore class="flex flex-wrap gap-1">
      <button
        v-for="section in SECTIONS"
        :key="`index-${section.key}`"
        type="button"
        class="px-2 py-1 text-xs rounded-md text-n-slate-11 hover:bg-n-alpha-1"
        @click="goToSection(section.key)"
      >
        {{
          t(`DASHBOARD.ATENDIMENTO.SECTIONS.${section.key.toUpperCase()}.TITLE`)
        }}
      </button>
    </nav>

    <div data-pdf-group class="flex flex-col gap-3">
      <section
        v-for="section in SECTIONS"
        :id="`painel-section-${section.key}`"
        :key="section.key"
        class="border rounded-lg border-n-weak"
      >
        <button
          type="button"
          class="flex items-center justify-between w-full gap-2 p-3 text-left"
          @click="toggleSection(section.key)"
        >
          <span class="flex items-center gap-2 font-medium text-n-slate-12">
            {{
              t(
                `DASHBOARD.ATENDIMENTO.SECTIONS.${section.key.toUpperCase()}.TITLE`
              )
            }}
            <span
              v-if="section.live"
              class="text-xs font-normal text-n-slate-10"
            >
              {{ t('DASHBOARD.ATENDIMENTO.LIVE_BADGE') }}
            </span>
          </span>
          <span
            class="flex-shrink-0 transition-transform"
            :class="[
              isExpanded(section.key)
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down',
            ]"
          />
        </button>
        <div v-if="isExpanded(section.key)" class="p-3 pt-0">
          <p
            v-if="section.needsPeriod && (!dateRange.since || !dateRange.until)"
            class="text-sm text-n-slate-11"
          >
            {{ t('DASHBOARD.ATENDIMENTO.PERIOD.CUSTOM_RANGE_INCOMPLETE') }}
          </p>
          <component
            :is="section.component"
            v-else-if="section.component"
            v-bind="sectionProps(section)"
          />
          <p v-else class="text-sm text-n-slate-11">
            {{ t('DASHBOARD.ATENDIMENTO.SECTION_PLACEHOLDER') }}
          </p>
        </div>
      </section>
    </div>
  </div>
</template>
